/**
 * categoryLabel.ts — Traducción reutilizable de categorías GMB EN→ES.
 *
 * Las categorías llegan del backend como slug GMB en inglés (p.ej. "lawyer",
 * "auto_repair_shop"). Este util las traduce a la etiqueta ES del catálogo
 * bilingüe oficial (`gmbCategoryLabels.generated.ts`, copia del catálogo del
 * backend). Si el slug no está en el catálogo, hace un fallback prettificando
 * el texto original (nunca rompe, nunca inventa).
 *
 * Frontend-only (sin round-trip ni cambio de backend). Lo usan la Home
 * (distribución por categoría, oportunidades del día) y la Búsqueda (tabla,
 * distribución, filtros).
 *
 * @module lib/categoryLabel
 */

import { GMB_CATEGORY_ES } from '@/lib/gmbCategoryLabels.generated'

/**
 * Traducciones que faltan en el catálogo generado (gap visible en widget de
 * búsquedas y tabla). Tienen prioridad sobre `GMB_CATEGORY_ES`. Mantener corto:
 * si el catálogo del backend las incorpora, se pueden retirar de aquí.
 */
const CATEGORY_OVERRIDES: Record<string, string> = {
  auto_repair_shop: 'Taller mecánico',
  auto_body_shop: 'Taller de chapa',
}

/** Prettifica un slug/etiqueta como fallback ("auto_repair_shop" → "Auto Repair Shop"). */
function prettify(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Devuelve la etiqueta ES de una categoría GMB. Acepta el slug en inglés
 * (id del catálogo) o, si ya viene una etiqueta, la prettifica. `null`/vacío
 * → 'Sin categoría'.
 */
export function categoryLabel(slug: string | null | undefined): string {
  if (!slug) return 'Sin categoría'
  const key = slug.trim().toLowerCase().replace(/\s+/g, '_')
  return (
    CATEGORY_OVERRIDES[key] ??
    GMB_CATEGORY_ES[key] ??
    GMB_CATEGORY_ES[slug.trim()] ??
    prettify(slug)
  )
}
